import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Typography, TextField, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Alert } from '@mui/material';

import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function AdminTechnicians() {
  const [techs, setTechs] = useState([]);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editFirst, setEditFirst] = useState('');
  const [editLast, setEditLast] = useState('');

  const handleToggleVisible = async (tech) => {
    setError('');
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/api/technicians/${tech._id}/visible`, { visible: !tech.visible });
      setMsg(`Technician ${!tech.visible ? 'shown' : 'hidden'} in field form!`);
      fetchTechs();
    } catch (e) {
      setError('Failed to update technician visibility');
    } finally {
      setSaving(false);
    }
  };


  const fetchTechs = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/technicians`);
      setTechs(res.data);
    } catch (e) {
      setError('Failed to fetch technicians');
    }
  };

  useEffect(() => {
    fetchTechs();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/technicians`, { firstName, lastName });
      setMsg('Technician added!');
      setFirstName('');
      setLastName('');
      fetchTechs();
    } catch (e) {
      setError('Failed to add technician');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setError('');
    try {
      await axios.delete(`${API_URL}/api/technicians/${id}`);
      setMsg('Technician removed!');
      fetchTechs();
    } catch (e) {
      setError('Failed to remove technician');
    }
  };

  const handleEdit = (tech) => {
    setEditingId(tech._id);
    setEditFirst(tech.firstName);
    setEditLast(tech.lastName);
  };

  const handleEditSave = async (id) => {
    setSaving(true);
    setError('');
    try {
      await axios.put(`${API_URL}/api/technicians/${id}`, { firstName: editFirst, lastName: editLast });
      setMsg('Technician updated!');
      setEditingId(null);
      setEditFirst('');
      setEditLast('');
      fetchTechs();
    } catch (e) {
      setError('Failed to update technician');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Manage Technicians</Typography>
      {msg && <Alert severity="success" sx={{ mb: 2 }}>{msg}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper sx={{ p: 2, mb: 3 }}>
        <form onSubmit={handleAdd} style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <TextField label="First Name" value={firstName} onChange={e => setFirstName(e.target.value)} required size="small" />
          <TextField label="Last Name" value={lastName} onChange={e => setLastName(e.target.value)} required size="small" />
          <Button type="submit" variant="contained" disabled={saving || !firstName || !lastName}>Add</Button>
        </form>
      </Paper>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Technician</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {techs.map(tech => (
              <TableRow key={tech._id}>
                <TableCell>
                  {editingId === tech._id ? (
                    <>
                      <TextField size="small" value={editFirst} onChange={e => setEditFirst(e.target.value)} sx={{ mr: 1 }} />
                      <TextField size="small" value={editLast} onChange={e => setEditLast(e.target.value)} />
                    </>
                  ) : (
                    `${tech.firstName} ${tech.lastName}`
                  )}
                </TableCell>
                <TableCell align="center">
                  <IconButton color={tech.visible ? "success" : "default"} onClick={() => handleToggleVisible(tech)} title={tech.visible ? "Hide from field form" : "Show in field form"}>
                    {tech.visible ? <VisibilityIcon /> : <VisibilityOffIcon />}
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
